import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Tooltip } from 'primereact/tooltip';
import { itemRemovalActions } from '../../reducers/itemRemoval';
import { InfoIcon } from '../SVGIcons';

interface RootState {
  itemRemoval: {
    furnitureRemovalMode: 'full' | 'partial';
    selectedItemImage?: number;
    itemImages: any[];
  };
}

interface FurnitureRemovalModeToggleProps {
  selectedImageUrl?: string;
  disabled?: boolean;
  partialDisabled?: boolean;  // 一部ボタンのみを無効にする（全体で処理失敗した場合など）
}

const modeTooltip = '・全体：画像内のすべての家具を自動検出して削除します。\n・一部：マスクで指定した領域の家具のみを削除します。';

/**
 * 家具消しモード切り替えトグルボタン
 * 「全体」と「一部」を切り替えるボタンコンポーネント
 */
const FurnitureRemovalModeToggle: React.FC<FurnitureRemovalModeToggleProps> = ({ selectedImageUrl, disabled = false, partialDisabled = false }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const furnitureRemovalMode = useSelector(
    (state: RootState) => state.itemRemoval.furnitureRemovalMode
  );

  const handleModeChange = (mode: 'full' | 'partial') => {
    if (disabled) return;
    dispatch(itemRemovalActions.setFurnitureRemovalMode(mode));

    // 「一部」モードに変更した時は描画ポップアップを開く
    if (mode === 'partial') {
      // 元画像URLを設定してから描画画面を開く
      if (selectedImageUrl) {
        dispatch(itemRemovalActions.setFurnitureRemovalOriginalImageUrl(selectedImageUrl));
      }
      dispatch(itemRemovalActions.setShowFurnitureRemovalDrawing(true));
    }
  };

  return (
    <>
    <style>{`
      .orange-tooltip-mode .p-tooltip-text {
        background-color: rgb(249 115 22);
        color: white;
        white-space: pre-line;
      }
      .orange-tooltip-mode .p-tooltip-arrow {
        border-right-color: rgb(249 115 22);
      }
    `}</style>
    <Tooltip target=".tooltip-furniture-mode" className="orange-tooltip-mode max-w-xs" />
    <div
      className="flex items-center gap-2"
    >
      <div
        className="flex gap-0 overflow-hidden"
        style={{
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          backgroundColor: 'white'
        }}
      >
      <button
        className="px-4 py-2 text-sm font-medium transition-all"
        style={{
          minWidth: '60px',
          backgroundColor: disabled ? '#e5e5e5' : (furnitureRemovalMode === 'full' ? '#f97316' : 'white'),
          color: disabled ? '#999' : (furnitureRemovalMode === 'full' ? 'white' : '#666'),
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: furnitureRemovalMode === 'full' ? 'none' : '1px solid #e0e0e0',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontWeight: furnitureRemovalMode === 'full' ? '600' : '500',
          opacity: disabled ? 0.6 : 1
        }}
        onClick={() => handleModeChange('full')}
        type="button"
        disabled={disabled}
        onMouseEnter={(e) => {
          if (!disabled && furnitureRemovalMode !== 'full') {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && furnitureRemovalMode !== 'full') {
            e.currentTarget.style.backgroundColor = 'white';
          }
        }}
      >
        全体
      </button>
      <button
        className="px-4 py-2 text-sm font-medium transition-all"
        style={{
          minWidth: '60px',
          backgroundColor: (disabled || partialDisabled) ? '#e5e5e5' : (furnitureRemovalMode === 'partial' ? '#f97316' : 'white'),
          color: (disabled || partialDisabled) ? '#999' : (furnitureRemovalMode === 'partial' ? 'white' : '#666'),
          borderTop: 'none',
          borderBottom: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          cursor: (disabled || partialDisabled) ? 'not-allowed' : 'pointer',
          fontWeight: furnitureRemovalMode === 'partial' ? '600' : '500',
          opacity: (disabled || partialDisabled) ? 0.6 : 1
        }}
        onClick={() => handleModeChange('partial')}
        type="button"
        disabled={disabled || partialDisabled}
        onMouseEnter={(e) => {
          if (!disabled && !partialDisabled && furnitureRemovalMode !== 'partial') {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !partialDisabled && furnitureRemovalMode !== 'partial') {
            e.currentTarget.style.backgroundColor = 'white';
          }
        }}
      >
        一部
      </button>
      </div>
      <InfoIcon
        className="tooltip-furniture-mode w-4 h-4 cursor-pointer text-gray-400"
        data-pr-tooltip={modeTooltip}
        data-pr-position="right"
      />
    </div>
    </>
  );
};

export default FurnitureRemovalModeToggle;
